'use client';

import { useState } from 'react';
import { Sun, Moon, Globe } from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';
import { usePathname, useRouter } from '@/i18n/navigation';
import { FlagIcon } from '@/components/ui/flag-icon';
import { BucketIcon } from '@/components/ui/bucket-icon';
import { useTheme } from '@/hooks/use-theme';
import { useNavigation } from '@/contexts/navigation-context';

const LANGUAGES = [
  { code: 'pt', label: 'Português (BR)', countryCode: 'br' },
  { code: 'pt-PT', label: 'Português (PT)', countryCode: 'pt' },
  { code: 'en', label: 'English', countryCode: 'us' },
  { code: 'es', label: 'Español', countryCode: 'es' },
  { code: 'it', label: 'Italiano', countryCode: 'it' },
  { code: 'fr', label: 'Français', countryCode: 'fr' },
  { code: 'de', label: 'Deutsch', countryCode: 'de' },
  { code: 'zh', label: '中文', countryCode: 'cn' },
  { code: 'ja', label: '日本語', countryCode: 'jp' },
  { code: 'ko', label: '한국어', countryCode: 'kr' },
  { code: 'ar', label: 'العربية', countryCode: 'sa' },
];

export function Header() {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const { navigateTo } = useNavigation();
  const pathname = usePathname();
  const { theme, toggleTheme } = useTheme();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);

  const changeLanguage = (newLocale: string) => {
    setIsLangMenuOpen(false);
    router.replace(pathname, { locale: newLocale });
  };

  const currentLang =
    LANGUAGES.find((l) => locale === l.code) ||
    LANGUAGES.find((l) => locale.startsWith(l.code));

  return (
    <header
      className="border-b border-border/50 bg-background/80 backdrop-blur-md sticky top-0 z-50"
      data-testid="app-header"
    >
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <button
          className="font-display font-semibold text-xl tracking-tight text-foreground hover:text-primary transition-colors cursor-pointer flex items-center gap-2"
          onClick={() => navigateTo('/')}
          data-testid="app-logo"
        >
          <BucketIcon className="w-6 h-6 text-primary" />
          <span className="text-gradient">{t('app_name')}</span>
        </button>

        <div className="flex items-center gap-1" data-testid="app-header-controls">
          <div className="relative" data-testid="lang-dropdown-wrapper">
            <button
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              className="p-2.5 rounded-xl hover:bg-secondary transition-all text-foreground flex items-center gap-2"
              aria-label={t('aria_change_language')}
              data-testid="lang-dropdown-trigger"
            >
              <span className="text-xl leading-none" data-testid="lang-current-flag">
                {currentLang ? (
                  <FlagIcon countryCode={currentLang.countryCode} size={22} />
                ) : (
                  <Globe className="w-5 h-5" />
                )}
              </span>
              <span
                className="text-xs uppercase font-semibold hidden sm:inline-block text-muted-foreground"
                data-testid="lang-current-code"
              >
                {currentLang?.code || locale.split('-')[0]}
              </span>
            </button>

            {isLangMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsLangMenuOpen(false)}
                />
                <div
                  className="absolute top-full right-0 rtl:right-auto rtl:left-0 mt-2 w-52 bg-card text-card-foreground rounded-2xl card-elevated border border-border py-2 z-50 animate-scale-in"
                  data-testid="lang-dropdown-menu"
                >
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full text-left rtl:text-right px-4 py-2.5 text-sm hover:bg-secondary transition-colors flex items-center gap-3 ${
                        locale === lang.code
                          ? 'text-primary font-semibold bg-primary/5'
                          : 'text-foreground'
                      }`}
                      data-testid={`lang-option-${lang.code}`}
                    >
                      <FlagIcon countryCode={lang.countryCode} size={20} />
                      {lang.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <button
            onClick={toggleTheme}
            className="p-2.5 rounded-xl hover:bg-secondary transition-all text-muted-foreground hover:text-foreground"
            aria-label={t('aria_toggle_theme')}
            data-testid="theme-toggle"
          >
            {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </header>
  );
}
